import type { MapBounds, MapboxBounds, MapCoordinate } from './types'

export type { MapBounds } from './types'

interface ParseMapBoundsOptions {
  preferLegacyWhenAmbiguous?: boolean
}

export function normaliseLongitude(longitude: number): number {
  if (longitude >= -180 && longitude <= 180) {
    return longitude
  }

  return ((longitude + 180) % 360 + 360) % 360 - 180
}

export function getLongitudeSpan(east: number, west: number): number {
  if (east < 0 && west > 0) {
    return (180 - west) + (180 + east)
  }

  return east - west
}

const EARTH_RADIUS_KM = 6371
const KM_PER_LATITUDE_DEGREE = (2 * Math.PI * EARTH_RADIUS_KM) / 360
const DEFAULT_BOUNDS_EPSILON = 0.0001

export function areBoundsClose(
  a: MapBounds,
  b: MapBounds,
  epsilon: number = DEFAULT_BOUNDS_EPSILON,
): boolean {
  return (
    Math.abs(a.north - b.north) < epsilon &&
    Math.abs(a.south - b.south) < epsilon &&
    Math.abs(a.east - b.east) < epsilon &&
    Math.abs(a.west - b.west) < epsilon
  )
}

export function getBoundsCenter(bounds: MapBounds): MapCoordinate {
  const lat = (bounds.north + bounds.south) / 2
  if (bounds.west <= bounds.east) {
    return { lat, lng: (bounds.east + bounds.west) / 2 }
  }
  return { lat, lng: normaliseLongitude((bounds.west + bounds.east + 360) / 2) }
}

function getLongitudeSpanKm(latitude: number, spanDegrees: number): number {
  const circumferenceAtLat = 2 * Math.PI * EARTH_RADIUS_KM * Math.cos((latitude * Math.PI) / 180)
  return circumferenceAtLat * (spanDegrees / 360)
}

export function getMaxVisibleKm(bounds: MapBounds): number {
  const midLat = (bounds.north + bounds.south) / 2
  const lonSpan = Math.min(Math.max(getLongitudeSpan(bounds.east, bounds.west), 0), 360)
  const latSpan = Math.min(Math.max(Math.abs(bounds.north - bounds.south), 0), 180)

  return Math.max(
    getLongitudeSpanKm(midLat, lonSpan),
    KM_PER_LATITUDE_DEGREE * latSpan,
  )
}

/**
 * Longitudes come back unwrapped, unlike every other producer here: these become corner coordinates
 * for a camera fit, where Mapbox reads 180.04 as just past the antimeridian and a normalised
 * -179.96 as a box the long way round.
 */
export function getBoundsAroundPoint(
  point: MapCoordinate,
  widthKm: number,
  heightKm: number,
): MapBounds {
  const halfLatDegrees = heightKm / 2 / KM_PER_LATITUDE_DEGREE
  const kmPerLngDegree = KM_PER_LATITUDE_DEGREE * Math.cos((point.lat * Math.PI) / 180)
  const halfLngDegrees = kmPerLngDegree > 0 ? widthKm / 2 / kmPerLngDegree : 0

  return {
    north: point.lat + halfLatDegrees,
    south: point.lat - halfLatDegrees,
    east: point.lng + halfLngDegrees,
    west: point.lng - halfLngDegrees,
  }
}

export function createMapBounds(coordinates: Array<MapCoordinate>): MapBounds | undefined {
  if (coordinates.length === 0) {
    return undefined
  }

  const latitudes = coordinates.map(({ lat }) => lat)
  const longitudes = coordinates
    .map(({ lng }) => normaliseLongitude(lng))
    .sort((left, right) => left - right)

  let west = longitudes[0]
  let east = longitudes[longitudes.length - 1]

  if (longitudes.length > 1) {
    let largestGap = -1
    let wrappedWestIndex = 0

    for (let index = 0; index < longitudes.length; index++) {
      const currentLongitude = longitudes[index]
      const nextLongitude = index === longitudes.length - 1
        ? longitudes[0] + 360
        : longitudes[index + 1]
      const gap = nextLongitude - currentLongitude

      if (gap > largestGap) {
        largestGap = gap
        wrappedWestIndex = (index + 1) % longitudes.length
      }
    }

    west = longitudes[wrappedWestIndex]
    east = longitudes[(wrappedWestIndex + longitudes.length - 1) % longitudes.length]
  }

  return {
    west,
    south: Math.min(...latitudes),
    east,
    north: Math.max(...latitudes),
  }
}

function isLongitudeWithinBounds(longitude: number, west: number, east: number): boolean {
  if (west <= east) {
    return longitude >= west && longitude <= east
  }

  return longitude >= west || longitude <= east
}

export function isCoordinateWithinBounds(coordinate: MapCoordinate, bounds: MapBounds): boolean {
  const normalisedLongitude = normaliseLongitude(coordinate.lng)

  return coordinate.lat >= bounds.south &&
    coordinate.lat <= bounds.north &&
    isLongitudeWithinBounds(normalisedLongitude, bounds.west, bounds.east)
}

export function mapBoundsToMapboxBounds(bounds: MapBounds): MapboxBounds {
  return [
    [bounds.west, bounds.south],
    [bounds.east, bounds.north],
  ]
}

export type MapboxBoundsLike = {
  getWest(): number
  getSouth(): number
  getEast(): number
  getNorth(): number
}

export function mapboxBoundsToMapBounds(bounds: MapboxBoundsLike): MapBounds {
  return {
    west: bounds.getWest(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    north: bounds.getNorth(),
  }
}

export function isFiniteMapBounds(bounds: MapBounds): boolean {
  return Number.isFinite(bounds.west) &&
    Number.isFinite(bounds.south) &&
    Number.isFinite(bounds.east) &&
    Number.isFinite(bounds.north)
}

export function isValidMapBounds(bounds: MapBounds): boolean {
  return isFiniteMapBounds(bounds) &&
    bounds.south >= -90 &&
    bounds.south <= 90 &&
    bounds.north >= -90 &&
    bounds.north <= 90 &&
    bounds.west >= -180 &&
    bounds.west <= 180 &&
    bounds.east >= -180 &&
    bounds.east <= 180 &&
    bounds.south <= bounds.north
}

export function expandMapBounds(
  bounds: MapBounds,
  latExpansion: number,
  lngExpansion: number,
): MapBounds {
  return {
    west: normaliseLongitude(bounds.west - lngExpansion),
    south: bounds.south - latExpansion,
    east: normaliseLongitude(bounds.east + lngExpansion),
    north: bounds.north + latExpansion,
  }
}

function createMapBoundsFromParts(
  south: number,
  west: number,
  north: number,
  east: number,
): MapBounds {
  return {
    west,
    south,
    east,
    north,
  }
}

export function parseMapBounds(
  bounds: string,
  coordinates: Array<MapCoordinate> = [],
  options: ParseMapBoundsOptions = {},
): MapBounds {
  const [first, second, third, fourth] = bounds.split(',').map(Number)

  const isLatitude = (value: number) => value >= -90 && value <= 90
  const isLongitude = (value: number) => value >= -180 && value <= 180
  const isCanonicalBounds = (
    south: number,
    west: number,
    north: number,
    east: number,
  ) => isLatitude(south) &&
    isLongitude(west) &&
    isLatitude(north) &&
    isLongitude(east) &&
    south <= north

  const candidates: Array<MapBounds> = []

  if (isCanonicalBounds(first, second, third, fourth)) {
    candidates.push(createMapBoundsFromParts(first, second, third, fourth))
  }

  // Temporary backwards compatibility for map bounds strings that were written
  // in west,south,east,north order during the migration. When both shapes are
  // valid, prefer the one that best matches the provided coordinates.
  if (isCanonicalBounds(second, first, fourth, third)) {
    const legacyCandidate = createMapBoundsFromParts(second, first, fourth, third)
    const isDuplicate = candidates.some(candidate =>
      candidate.west === legacyCandidate.west &&
      candidate.south === legacyCandidate.south &&
      candidate.east === legacyCandidate.east &&
      candidate.north === legacyCandidate.north,
    )

    if (!isDuplicate) {
      candidates.push(legacyCandidate)
    }
  }

  if (candidates.length === 0) {
    return createMapBoundsFromParts(first, second, third, fourth)
  }

  if (candidates.length === 1) {
    return candidates[0]
  }

  if (coordinates.length === 0) {
    return options.preferLegacyWhenAmbiguous ? candidates[candidates.length - 1] : candidates[0]
  }

  let bestCandidate = candidates[0]
  let bestMatchCount = coordinates.filter(coordinate => isCoordinateWithinBounds(coordinate, bestCandidate)).length

  for (let index = 1; index < candidates.length; index++) {
    const candidate = candidates[index]
    const matchCount = coordinates.filter(coordinate => isCoordinateWithinBounds(coordinate, candidate)).length

    if (matchCount > bestMatchCount) {
      bestCandidate = candidate
      bestMatchCount = matchCount
    }
  }

  return bestCandidate
}

export function serialiseMapBounds(bounds: MapBounds): string {
  return `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`
}

export function canonicalizeMapBoundsLongitudes(bounds: MapBounds): MapBounds {
  return {
    ...bounds,
    west: normaliseLongitude(bounds.west),
    east: normaliseLongitude(bounds.east),
  }
}

export function getMapViewportBounds(mapboxBounds: MapboxBoundsLike): MapBounds | undefined {
  const bounds = mapboxBoundsToMapBounds(mapboxBounds)

  if (!isFiniteMapBounds(bounds)) {
    return undefined
  }

  const canonicalBounds = canonicalizeMapBoundsLongitudes(bounds)

  return isValidMapBounds(canonicalBounds) ? canonicalBounds : undefined
}
